import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription }                 from 'rxjs';
import { TranslateService }             from '@ngx-translate/core';

import { chartColor, DONUT_COLOR_COUNT, donutColor, donutOtherColor } from '../../shared/components/charts/chart-palette';
import { ChartPointModel }                                            from '../../shared/components/charts/typings/chart-point';
import { ChartSeriesModel }                                           from '../../shared/components/charts/typings/chart-series';
import { DonutSegmentModel }                                          from '../../shared/components/charts/typings/donut-segment';
import { IntelligenceFormatService }                                  from '../intelligence.format.service';
import { IntelligenceService }                                        from '../intelligence.service';
import { IntelligenceVehicleFiltersModel }                            from '../typings/intelligence.vehicle-filters';
import { IntelligenceVehicleMakeModel }                               from '../typings/intelligence.vehicle-make';
import { IntelligenceVehiclesModel }                                  from '../typings/intelligence.vehicles';
import { IntelligenceVehicleTypeModel }                               from '../typings/intelligence.vehicle-type';
import { TableSortModel }                                             from '../../shared/components/charts/typings/table-sort';

interface VehicleFilterOptions {
  body_types : Array<string>;
  drive_types: Array<string>;
  fuel_types : Array<string>;
  makes      : Array<string>;
}

@Component({
  selector   : 'wor-intelligence-vehicles-tab',
  template: require('./intelligence.vehicles-tab.component.html')
})
export class VehiclesTabComponent implements OnInit, OnDestroy {
  public error   : boolean = false;
  public loading : boolean = true;

  public avgSalePoints       : Array<ChartPointModel> = [];
  public driveTypeSegments   : Array<DonutSegmentModel> = [];
  public effectivenessPoints : Array<ChartPointModel> = [];
  public filterOptions       : VehicleFilterOptions = null;
  public filters             : IntelligenceVehicleFiltersModel = { body_type: '', drive_type: '', fuel_type: '', make: '' };
  public fuelTypeSegments    : Array<DonutSegmentModel> = [];
  public makes               : Array<IntelligenceVehicleMakeModel> = [];
  public segments            : Array<DonutSegmentModel> = [];
  public sort                : TableSortModel = { direction: 'desc', field: 'revenue_ex_tax' };
  public trendPoints         : Array<ChartPointModel> = [];

  public avgSaleSeries : Array<ChartSeriesModel> = [
    { color: chartColor(1), key: 'avg_sale_ex_tax', label: this.translateService.instant('INTELLIGENCE_SPACE.KPIS.AVG_INVOICE') }
  ];

  public effectivenessSeries : Array<ChartSeriesModel> = [
    { color: chartColor(0), key: 'messages_sent', label: this.translateService.instant('INTELLIGENCE_SPACE.VEHICLES.SERVICE_MESSAGES') },
    { color: chartColor(1), key: 'services_completed', label: this.translateService.instant('INTELLIGENCE_SPACE.VEHICLES.SERVICES_COMPLETED') }
  ];

  public trendSeries : Array<ChartSeriesModel> = [
    { color: chartColor(1), key: 'invoice_count', label: this.translateService.instant('INTELLIGENCE_SPACE.COMMON.INVOICES') },
    { color: chartColor(0), key: 'unique_vehicles', label: this.translateService.instant('INTELLIGENCE_SPACE.VEHICLES.UNIQUE_VEHICLES') }
  ];

  private loadGeneration : number = 0;
  private subscription   : Subscription;

  constructor (
    private intelligenceFormatService: IntelligenceFormatService,
    public intelligenceService       : IntelligenceService,
    private translateService         : TranslateService
  ) {}

  ngOnInit () : void {
    this.load();

    this.subscription = this.intelligenceService.rangeChanged.subscribe(() => this.load());
  }

  ngOnDestroy () : void {
    this.subscription.unsubscribe();
  }

  clearFilters () : void {
    this.filters = { body_type: '', drive_type: '', fuel_type: '', make: '' };

    this.load();
  }

  hasActiveFilters () : boolean {
    return Object.keys(this.filters).some(key => !!this.filters[ key ]);
  }

  count ( value : number ) : string {
    return this.intelligenceFormatService.count(value);
  }

  dayLabel () : string {
    return this.intelligenceService.previousRange
      ? this.intelligenceFormatService.date(this.intelligenceService.range.start_date)
      : '';
  }

  grossProfit ( make : IntelligenceVehicleMakeModel ) : number {
    return make.revenue_ex_tax - make.cost_ex_tax;
  }

  isAvgSaleEmpty () : boolean {
    return !this.avgSalePoints.some(point => point.values.avg_sale_ex_tax > 0);
  }

  isDriveTypesEmpty () : boolean {
    return !this.driveTypeSegments.some(segment => segment.value > 0);
  }

  isEffectivenessEmpty () : boolean {
    return !this.effectivenessPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  isEmpty () : boolean {
    return !this.makes.length;
  }

  isFuelTypesEmpty () : boolean {
    return !this.fuelTypeSegments.some(segment => segment.value > 0);
  }

  isTrendEmpty () : boolean {
    return !this.trendPoints.some(point => Object.keys(point.values).some(key => point.values[ key ] !== 0));
  }

  load () : void {
    const generation = ++this.loadGeneration;

    this.error = false;
    this.loading = true;

    this.intelligenceService.getVehicles(this.filters)
    .then(( data : IntelligenceVehiclesModel ) => {
      if ( generation !== this.loadGeneration ) {
        return;
      }

      this.makes = data.makes || [];

      // Guarded so a frontend that reaches an environment before the backend does
      // keeps its last known lists rather than blanking every dropdown.
      if ( data.filter_options ) {
        this.filterOptions = {
          body_types : data.filter_options.body_types || [],
          drive_types: data.filter_options.drive_types || [],
          fuel_types : data.filter_options.fuel_types || [],
          makes      : data.filter_options.makes || []
        };

        if ( this.dropFiltersMissingFromOptions() ) {
          this.load();

          return;
        }
      }

      const top = this.makes.slice(0, DONUT_COLOR_COUNT);

      this.segments = top.map(( make, index ) => ({
        color      : donutColor(make.vehicle_make, this.filterOptions.makes, index),
        isDrillable: true,
        label      : make.vehicle_make,
        value      : make.revenue_ex_tax
      }));

      if ( this.makes.length > top.length ) {
        this.segments.push({
          color : donutOtherColor(),
          label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
          others: this.makes.slice(top.length).map(( make, index ) => ({
            color      : donutColor(make.vehicle_make, this.filterOptions.makes, DONUT_COLOR_COUNT + index),
            isDrillable: true,
            label      : make.vehicle_make,
            value      : make.revenue_ex_tax
          })),
          value : this.makes.slice(top.length).reduce(( sum, make ) => sum + make.revenue_ex_tax, 0)
        });
      }

      this.trendPoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          invoice_count  : day.invoice_count,
          unique_vehicles: day.unique_vehicles
        }
      }));

      this.avgSalePoints = ( data.series || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: { avg_sale_ex_tax: day.avg_sale_ex_tax || 0 }
      }));

      this.effectivenessPoints = ( data.service_effectiveness || [] ).map(day => ({
        key   : day.date,
        label : this.intelligenceFormatService.date(day.date),
        values: {
          messages_sent     : day.messages_sent,
          services_completed: day.services_completed
        }
      }));

      this.fuelTypeSegments = this.buildTypeSegments(data.fuel_types || [], true, this.filterOptions.fuel_types);
      this.driveTypeSegments = this.buildTypeSegments(data.drive_types || [], true, this.filterOptions.drive_types);
    })
    .catch(() => {
      if ( generation === this.loadGeneration ) {
        this.error = true;
      }
    })
    .finally(() => {
      if ( generation === this.loadGeneration ) {
        this.loading = false;
      }
    });
  }

  money ( value : number ) : string {
    return this.intelligenceFormatService.money(value);
  }

  restoreDay () : void {
    this.intelligenceService.restoreRange();
  }

  selectDay ( point : ChartPointModel ) : void {
    this.intelligenceService.selectDay(point.key);
  }

  setFilter ( field : keyof IntelligenceVehicleFiltersModel, value : string ) : void {
    this.filters[ field ] = value;

    this.load();
  }

  sortBy ( field : string ) : void {
    this.sort = {
      direction: this.sort.field === field && this.sort.direction === 'desc'
        ? 'asc'
        : 'desc',
      field
    };

    this.makes = this.intelligenceFormatService.sortRows(this.makes, this.sort.field, this.sort.direction);
  }

  toggleFilter ( field : keyof IntelligenceVehicleFiltersModel, value : string ) : void {
    this.setFilter(field, this.filters[ field ] === value
      ? ''
      : value);
  }

  // Reports whether anything was dropped: the payload that carried these options was
  // narrowed by a filter this period has no option for, so the caller has to ask again
  // before showing it.
  private dropFiltersMissingFromOptions () : boolean {
    const kept = {
      body_type : this.intelligenceFormatService.keepIfOffered(this.filters.body_type, this.filterOptions.body_types),
      drive_type: this.intelligenceFormatService.keepIfOffered(this.filters.drive_type, this.filterOptions.drive_types),
      fuel_type : this.intelligenceFormatService.keepIfOffered(this.filters.fuel_type, this.filterOptions.fuel_types),
      make      : this.intelligenceFormatService.keepIfOffered(this.filters.make, this.filterOptions.makes)
    };
    const dropped = Object.keys(kept).some(key => kept[ key ] !== this.filters[ key ]);

    this.filters = { ...this.filters, ...kept };

    return dropped;
  }

  private buildTypeSegments ( types : Array<IntelligenceVehicleTypeModel>, isDrillable : boolean = false, options : Array<string> = null ) : Array<DonutSegmentModel> {
    const kept = types.slice(0, DONUT_COLOR_COUNT);
    const remainder = types.slice(DONUT_COLOR_COUNT).reduce(( sum, type ) => sum + type.revenue_ex_tax, 0);

    const segments : Array<DonutSegmentModel> = kept.map(( type, index ) => ({
      color: donutColor(type.label, options, index),
      isDrillable,
      label: type.label,
      value: type.revenue_ex_tax
    }));

    if ( remainder > 0 ) {
      segments.push({
        color : donutOtherColor(),
        label : this.translateService.instant('INTELLIGENCE_SPACE.COMMON.OTHER'),
        others: types.slice(DONUT_COLOR_COUNT).map(( type, index ) => ({
          color: donutColor(type.label, options, DONUT_COLOR_COUNT + index),
          isDrillable,
          label: type.label,
          value: type.revenue_ex_tax
        })),
        value : remainder
      });
    }

    return segments;
  }
}
